import { Injectable } from '@angular/core';
import { ApiBaseService } from '@core/services/api-base.service';
import { Mapper } from '@core/services/mapper/base-mapper.mapper';
import { combineLatest, Observable, of, zip } from 'rxjs';
import { filter, map, startWith, tap } from 'rxjs/operators';
import { ApiConstant } from '@shared/constants/api.constant';
import { MediaType } from '@shared/enums/media-type.enum';
import { Media } from '@shared-features/models/media';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import { FileWithMedia } from '@shared/utils/file-with-media.util';

@Injectable({
  providedIn: 'root',
})
export class MediaApiService {
  constructor(private baseAPI: ApiBaseService, private mapper: Mapper) {}
  getMedia(id: number): Observable<Media> {
    return this.baseAPI
      .get(`${ApiConstant.GET_MEDIA_BY_ID}${id}`)
      .pipe(map((res) => this.mapper.fromJson(Media, res.data)));
  }

  uploadMedia(file: File, type: MediaType): Observable<Media[]>;
  uploadMedia(
    file: File,
    type: MediaType,
    reportProgress: true
  ): Observable<{ media: Media; progress: number; file: File }>;

  uploadMedia(
    file: File,
    type: MediaType,
    reportProgress?: boolean
  ): Observable<Media[] | { media: Media; progress: number; file: File }> {
    const formData: FormData = new FormData();
    formData.append('media', file, file.name);
    formData.append('type', type);
    const headers = new Headers();
    headers.append('Content-Type', 'multipart/form-data');
    headers.append('Accept', 'application/json');
    const progressOption = reportProgress ? { reportProgress: true, observe: 'events' } : {};
    const uploadRequest$ = this.baseAPI.post(ApiConstant.UPLOAD_MEDIA, formData, {
      headers,
      ...progressOption,
    });
    if (!reportProgress) {
      return uploadRequest$.pipe(
        map((res) => {
          return this.mapper.fromJson(Media, res.data);
        })
      );
    }
    return uploadRequest$.pipe(
      map((event: HttpEvent<any>) => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress = Math.round((100 * event.loaded) / event.total);
            return { media: null, progress, file };
          case HttpEventType.Response:
            const media = this.mapper.fromJson(Media, event.body.data?.[0]);
            return { media, progress: 100, file: media ? new FileWithMedia(media) : file };
          default:
            return { media: null, progress: 0, file };
        }
      })
    );
  }
  uploadMedias(files: File[], type: MediaType): Observable<Media[][]>;
  uploadMedias(
    files: File[],
    type: MediaType,
    onProgress: (progresses: number[], files: (File | FileWithMedia)[]) => void
  ): Observable<{ media: Media; progress: number; file: File }[]>;

  uploadMedias(
    files: File[],
    type: MediaType,
    onProgress?: (progresses: number[], files: (File | FileWithMedia)[]) => void
  ): Observable<Media[][] | { media: Media; progress: number; file: File | FileWithMedia }[]> {
    if (!onProgress) {
      return zip(
        ...(files || []).filter((file) => file).map((file) => this.uploadMedia(file, type))
      );
    }
    return combineLatest(
      ...(files || []).map((file: File | FileWithMedia) =>
        (file as FileWithMedia)?.media
          ? of({ progress: 100, media: (file as FileWithMedia).media, file })
          : this.uploadMedia(file, type, true).pipe(startWith({ media: null, progress: 0, file }))
      )
    ).pipe(
      tap((res) => this.onProgressHandler(res, onProgress)),
      filter((res) => res.every((item) => item.progress === 100 && item.media))
    );
  }

  /**
   * instead of duplication to get files (original during upload or with media after upload);
   * this will be used as source for control of files value and progresses too.
   */
  private onProgressHandler(
    res: { progress: number; file: File | FileWithMedia }[],
    onProgress?: (progresses: number[], files: (File | FileWithMedia)[]) => void
  ) {
    if (!onProgress) {
      return;
    }
    const [progresses, files] = res.reduce(
      ([progresses, files], item) => {
        progresses.push(item.progress);
        files.push(item.file);
        return [progresses, files];
      },
      [[], []]
    );

    onProgress(progresses, files);
  }
}
